import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  Link as MuiLink,
  useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../theme';
import api from '../api/client';

const ForgotPassword = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm();

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.post('/auth/forgot-password', {
        email: data.email
      });

      setSuccess(t('resetLinkSent') || 'If an account exists with this email, a password reset link has been sent. Please check your inbox.');
    } catch (err) {
      console.error('Forgot password error:', err);
      setError(t('resetLinkError') || 'Failed to send reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="80vh"
      p={2}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          backgroundColor: colors.grey[800]
        }}
      >
        <Typography variant="h3" fontWeight="bold" mb={2} textAlign="center">
          {t('forgotPassword') || 'Forgot Password'}
        </Typography>

        <Typography variant="body2" color={colors.grey[100]} mb={3} textAlign="center">
          {t('forgotPasswordDescription') || 'Enter your email address and we\'ll send you a link to reset your password.'}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <TextField
            fullWidth
            label={t('email') || 'Email'}
            type="email"
            margin="normal"
            {...register('email', {
              required: t('emailRequired') || 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: t('invalidEmail') || 'Invalid email address'
              }
            })}
            error={!!errors.email}
            helperText={errors.email?.message}
            disabled={loading || success}
          />

          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={loading || success}
            sx={{ mt: 3, mb: 2, py: 1.5 }}
          >
            {loading ? t('sendingLink') || 'Sending...' : t('sendResetLink') || 'Send Reset Link'}
          </Button>

          <Box textAlign="center">
            <Typography variant="body2" color={colors.grey[100]}>
              {t('rememberPassword') || 'Remember your password?'}{' '}
              <MuiLink
                component={Link}
                to="/login"
                sx={{ color: colors.blueAccent[400], textDecoration: 'none' }}
              >
                {t('login') || 'Login'}
              </MuiLink>
            </Typography>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default ForgotPassword;
